// src/components/ContactModal.tsx — Phase 1 Contacts slice, simplified after
// Phase 9 schema cleanup.
//
// Role flags (DM/MOM/EM/Manager/News/Alerts) and emergency-contact details
// no longer live in this modal:
//   - Role flags moved to hub_users in migration 032/033; admins manage them
//     via the User Access page.
//   - Emergency contact lives on hub_personal_details, owned by each user
//     via their own Me → Personal Details page.
//
// What remains: name, phone, email, role/title, location. Three modes:
// create, edit, delete-confirm.
//
// Field shape and POST/PATCH semantics mirror api.md §4.2 verbatim:
//   - Create: POST omits empty strings entirely.
//   - Edit:   PATCH sends only changed fields; an emptied string becomes
//             explicit `null` so the column is cleared.

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { apiFetch, ApiError } from "../api";
import { C, F, layout } from "../theme";
import { Button, Card, Eyebrow, Field, TopBar } from "./ui";

// ---------------------------------------------------------------------------
// Public types — kept compatible with ContactsPage's list row
// ---------------------------------------------------------------------------

export type ContactCategory = "nymr" | "volunteer" | "personal";

export interface ContactRow {
  id: number;
  first: string;
  last: string;
  phone: string | null;
  email: string | null;
  role: string | null;
  location: string | null;
  /** Projected from hub_users via the email join (read-only here). */
  isDM: boolean;
  isMOM: boolean;
  isEM: boolean;
  isDF: boolean;
  isManager: boolean;
  isNewsAdmin: boolean;
  isAlerts: boolean;
  /** True when an emergency contact is on file for the linked hub_user. */
  hasEmergency: boolean;
  /** Slice G: per-user pin state from /api/contacts. */
  isPinned: boolean;
  /** Phase 7: linked hub_users.id (server-matched by lowercased email). */
  hubUserId: number | null;
  /** Phase 9: contact category. */
  category: ContactCategory;
  /** Phase 9: hub_users.id of the personal-contact owner; null for directory. */
  ownerUserId: number | null;
  /** Phase 9: first-aider glyph indicator. Projected from hub_users. */
  isFirstAider: boolean;
  /** Sign-In System: linked hub_user has an open duty session right now.
   *  Optional — only the /api/contacts list projects it. */
  signedOn?: boolean;
}

export type ContactModalMode =
  | { kind: "create"; category?: ContactCategory }
  | { kind: "edit"; contact: ContactRow }
  | { kind: "delete-confirm"; contact: ContactRow };

export type ContactSavedOp =
  | { kind: "created"; row: ContactRow; warning?: string }
  | { kind: "updated"; row: ContactRow }
  | { kind: "deleted"; id: number };

interface Props {
  mode: ContactModalMode;
  onClose: () => void;
  onSaved: (op: ContactSavedOp) => void;
  // Edit form's Delete button calls this; parent re-routes to delete-confirm.
  onRequestDelete?: (contact: ContactRow) => void;
}

// ---------------------------------------------------------------------------
// Form state -- just the six string fields the modal owns
// ---------------------------------------------------------------------------

interface FormState {
  first: string;
  last: string;
  phone: string;
  email: string;
  role: string;
  location: string;
}

const EMPTY_FORM: FormState = {
  first: "",
  last: "",
  phone: "",
  email: "",
  role: "",
  location: "",
};

const STRING_KEYS = [
  "first",
  "last",
  "phone",
  "email",
  "role",
  "location",
] as const;

interface ContactDetailResponse {
  id: number;
  first: string;
  last: string;
  phone: string | null;
  email: string | null;
  role: string | null;
  location: string | null;
  category?: ContactCategory;
  ownerUserId?: number | null;
  isFirstAider?: boolean;
}

function formFromRow(row: ContactRow): FormState {
  return {
    first: row.first ?? "",
    last: row.last ?? "",
    phone: row.phone ?? "",
    email: row.email ?? "",
    role: row.role ?? "",
    location: row.location ?? "",
  };
}

function formFromDetail(d: ContactDetailResponse): FormState {
  return {
    first: d.first ?? "",
    last: d.last ?? "",
    phone: d.phone ?? "",
    email: d.email ?? "",
    role: d.role ?? "",
    location: d.location ?? "",
  };
}

function rowFromForm(
  form: FormState,
  id: number,
  opts?: {
    category?: ContactCategory;
    ownerUserId?: number | null;
    existing?: ContactRow | null;
  },
): ContactRow {
  const trim = (s: string) => {
    const t = s.trim();
    return t === "" ? null : t;
  };
  const e = opts?.existing ?? null;
  return {
    id,
    first: form.first.trim(),
    last: form.last.trim(),
    phone: trim(form.phone),
    email: trim(form.email),
    role: trim(form.role),
    location: trim(form.location),
    // Server-owned flags carry over from the original row so stamps don't
    // blink between save and the next list refresh. The modal doesn't edit
    // these any more — they live on hub_users.
    isDM: e?.isDM ?? false,
    isMOM: e?.isMOM ?? false,
    isEM: e?.isEM ?? false,
    isDF: e?.isDF ?? false,
    isManager: e?.isManager ?? false,
    isNewsAdmin: e?.isNewsAdmin ?? false,
    isAlerts: e?.isAlerts ?? false,
    hasEmergency: e?.hasEmergency ?? false,
    isPinned: e?.isPinned ?? false,
    hubUserId: e?.hubUserId ?? null,
    category: opts?.category ?? e?.category ?? "nymr",
    ownerUserId: opts?.ownerUserId ?? e?.ownerUserId ?? null,
    isFirstAider: e?.isFirstAider ?? false,
  };
}

function buildCreatePayload(
  form: FormState,
  category: ContactCategory,
): Record<string, string> {
  const payload: Record<string, string> = {
    first: form.first.trim(),
    last: form.last.trim(),
    category,
  };
  for (const k of STRING_KEYS) {
    if (k === "first" || k === "last") continue;
    const v = form[k].trim();
    if (v) payload[k] = v;
  }
  return payload;
}

function buildPatchPayload(
  form: FormState,
  initial: FormState,
): Record<string, string | null> {
  const payload: Record<string, string | null> = {};
  for (const k of STRING_KEYS) {
    const newV = form[k].trim();
    const oldV = initial[k].trim();
    if (newV === oldV) continue;
    payload[k] = newV === "" ? null : newV;
  }
  return payload;
}

// ---------------------------------------------------------------------------
// Top-level component — routes by mode
// ---------------------------------------------------------------------------

export function ContactModal({ mode, onClose, onSaved, onRequestDelete }: Props) {
  const busyRef = useRef(false);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busyRef.current) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (mode.kind === "delete-confirm") {
    return (
      <Overlay>
        <DeleteBody
          contact={mode.contact}
          onClose={onClose}
          onSaved={onSaved}
          busyRef={busyRef}
        />
      </Overlay>
    );
  }

  return (
    <Overlay>
      <FormBody
        mode={mode}
        onClose={onClose}
        onSaved={onSaved}
        onRequestDelete={onRequestDelete}
        busyRef={busyRef}
      />
    </Overlay>
  );
}

// ---------------------------------------------------------------------------
// Overlay shell
// ---------------------------------------------------------------------------

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: C.paper,
        zIndex: 100,
        overflowY: "auto",
      }}
    >
      <div style={{ maxWidth: layout.maxContentPx, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit form
// ---------------------------------------------------------------------------

function FormBody({
  mode,
  onClose,
  onSaved,
  onRequestDelete,
  busyRef,
}: {
  mode: Extract<ContactModalMode, { kind: "create" | "edit" }>;
  onClose: () => void;
  onSaved: (op: ContactSavedOp) => void;
  onRequestDelete?: (contact: ContactRow) => void;
  busyRef: React.MutableRefObject<boolean>;
}) {
  const isEdit = mode.kind === "edit";
  const editingId = isEdit ? mode.contact.id : null;
  const editingContact = isEdit ? mode.contact : null;
  const category: ContactCategory = isEdit
    ? mode.contact.category
    : (mode.category ?? "nymr");
  const isPersonal = category === "personal";

  const initialFromRow = useMemo<FormState>(
    () => (isEdit ? formFromRow(mode.contact) : EMPTY_FORM),
    [isEdit, mode],
  );

  const [form, setForm] = useState<FormState>(initialFromRow);
  const [initial, setInitial] = useState<FormState>(initialFromRow);
  const [detailLoading, setDetailLoading] = useState<boolean>(isEdit);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  busyRef.current = submitting;

  useEffect(() => {
    if (!isEdit || editingId == null) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    apiFetch<ContactDetailResponse>(`/contacts/${editingId}`)
      .then((d) => {
        if (cancelled) return;
        const next = formFromDetail(d);
        setForm(next);
        setInitial(next);
        setDetailLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message =
          e instanceof ApiError ? e.message : "Failed to load contact details";
        setDetailError(message);
        setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, editingId]);

  function setString(key: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function clientValidate(): string | null {
    if (form.first.trim() === "") return "First name is required";
    if (form.last.trim() === "") return "Last name is required";
    return null;
  }

  async function onSubmit() {
    if (submitting) return;
    setSubmitError(null);

    const clientError = clientValidate();
    if (clientError) {
      setSubmitError(clientError);
      return;
    }

    if (isEdit) {
      const payload = buildPatchPayload(form, initial);
      if (Object.keys(payload).length === 0) {
        setSubmitError("No changes to save");
        return;
      }
      setSubmitting(true);
      try {
        await apiFetch<{ ok: true }>(`/contacts/${editingId}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        const row = rowFromForm(form, editingId!, { existing: editingContact });
        onSaved({ kind: "updated", row });
      } catch (err: unknown) {
        const message = err instanceof ApiError ? err.message : "Failed to save changes";
        setSubmitError(message);
        setSubmitting(false);
      }
      return;
    }

    const payload = buildCreatePayload(form, category);
    setSubmitting(true);
    try {
      const res = await apiFetch<{ id: number; warning?: string }>(`/contacts`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const row = rowFromForm(form, res.id, { category });
      onSaved({ kind: "created", row, warning: res.warning });
    } catch (err: unknown) {
      if (err instanceof ApiError && typeof err.body === "object" && err.body !== null) {
        const b = err.body as { error?: string; message?: string };
        if (b.error === "PERSONAL_CONTACT_CAP") {
          setSubmitError(
            b.message ??
              "You've hit the 200 personal contact limit. Delete unused contacts to add new ones.",
          );
        } else {
          setSubmitError(b.message ?? err.message);
        }
      } else {
        setSubmitError(err instanceof ApiError ? err.message : "Failed to create contact");
      }
      setSubmitting(false);
    }
  }

  const title = isEdit
    ? isPersonal
      ? "Edit personal contact"
      : "Edit contact"
    : isPersonal
      ? "New personal contact"
      : "New contact";

  return (
    <div>
      <TopBar
        title={title}
        sub="Name, phone, email, role"
        onBack={submitting ? undefined : onClose}
      />
      <div style={{ padding: "16px 16px 32px" }}>
        {detailError && (
          <Card>
            <div
              style={{
                fontFamily: F.body,
                fontSize: 14,
                fontWeight: 700,
                color: C.signalDk,
              }}
            >
              {detailError}
            </div>
          </Card>
        )}

        <Card>
          <Field
            label="First name"
            value={form.first}
            onChange={(v) => setString("first", v)}
            disabled={submitting}
          />
          <Field
            label="Last name"
            value={form.last}
            onChange={(v) => setString("last", v)}
            disabled={submitting}
          />
          <Field
            label="Phone"
            value={form.phone}
            onChange={(v) => setString("phone", v)}
            type="tel"
            disabled={submitting || detailLoading}
          />
          <Field
            label="Email"
            value={form.email}
            onChange={(v) => setString("email", v)}
            type="email"
            disabled={submitting || detailLoading}
          />
          <Field
            label="Role / job title"
            value={form.role}
            onChange={(v) => setString("role", v)}
            disabled={submitting || detailLoading}
          />
          <Field
            label="Location"
            value={form.location}
            onChange={(v) => setString("location", v)}
            disabled={submitting || detailLoading}
          />

          {!isPersonal && (
            <div
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                fontSize: 12,
                color: C.inkMute,
                marginTop: 6,
                marginBottom: 4,
                lineHeight: 1.45,
              }}
            >
              Role flags (DM/MOM/EM/Manager/News/Alerts) and emergency-contact details
              live on the linked user. Set role flags in User Access; emergency contact
              is owned by the user themselves via Me → Personal Details.
            </div>
          )}

          {submitError && (
            <div
              style={{
                marginTop: 10,
                marginBottom: 12,
                padding: "10px 12px",
                background: C.signalBg,
                border: "1px solid rgba(184,58,43,0.22)",
                borderRadius: 10,
                fontFamily: F.body,
                fontSize: 13.5,
                fontWeight: 700,
                color: C.signalDk,
              }}
            >
              {submitError}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={submitting || detailLoading}
              style={{ flex: 1 }}
            >
              {submitting
                ? "Saving…"
                : isEdit
                  ? "Save changes"
                  : "Create contact"}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </div>

          {isEdit && editingContact && onRequestDelete && (
            <div style={{ marginTop: 14 }}>
              <Button
                variant="danger"
                fullWidth
                disabled={submitting}
                onClick={() => onRequestDelete(editingContact)}
              >
                Delete contact
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete-confirm body
// ---------------------------------------------------------------------------

function DeleteBody({
  contact,
  onClose,
  onSaved,
  busyRef,
}: {
  contact: ContactRow;
  onClose: () => void;
  onSaved: (op: ContactSavedOp) => void;
  busyRef: React.MutableRefObject<boolean>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  busyRef.current = submitting;

  async function onDelete() {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch<{ ok: true }>(`/contacts/${contact.id}`, { method: "DELETE" });
      onSaved({ kind: "deleted", id: contact.id });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          "This contact is on the duty rota. Remove their duty assignments before deleting.",
        );
      } else {
        setError(err instanceof ApiError ? err.message : "Failed to delete contact");
      }
      setSubmitting(false);
    }
  }

  const fullName = `${contact.first} ${contact.last}`.trim();

  const metaStyle: CSSProperties = {
    fontFamily: F.body,
    fontSize: 13.5,
    color: C.inkSoft,
    marginTop: 6,
    fontWeight: 600,
  };

  return (
    <div>
      <TopBar
        title="Delete contact"
        sub={fullName}
        onBack={submitting ? undefined : onClose}
      />
      <div style={{ padding: "16px 16px 32px" }}>
        <Card>
          <Eyebrow style={{ marginBottom: 8 }}>Confirm deletion</Eyebrow>
          <div
            style={{
              fontFamily: F.serif,
              fontSize: 18,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.2,
            }}
          >
            Permanently delete {fullName}?
          </div>
          <div style={metaStyle}>
            This cannot be undone.
            {(contact.role || contact.location) && (
              <span>
                {" "}
                {[contact.role, contact.location].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                background: C.signalBg,
                border: "1px solid rgba(184,58,43,0.22)",
                borderRadius: 10,
                fontFamily: F.body,
                fontSize: 13.5,
                fontWeight: 700,
                color: C.signalDk,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <Button
              variant="danger"
              onClick={onDelete}
              disabled={submitting}
              style={{ flex: 1 }}
            >
              {submitting ? "Deleting…" : "Delete contact"}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
